import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { useEffect, useState, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RemoveFormFieldButton } from '@/components/Buttons/RemoveFormFieldButton/RemoveFormFieldButton';
import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import { FormReadOnlyField } from '@/components/Form/FormReadOnlyField/FormReadOnlyField';
import { FormTextField } from '@/components/Form/FormTextField/FormTextField';
import { useDcrFields } from '@/features/DataChangeRequest/_constants/useDcrFields';
import { useDcrContext } from '@/features/DataChangeRequest/_context/DcrContext';
import { useDirtyFieldCheck } from '@/features/DataChangeRequest/_hooks/useDirtyFieldCheck';
import type { HcpDcrValuesIn } from '@/features/DataChangeRequest/_schemas/hcpSchema';
import { LICENSING_AUTHORITY_MAX_LENGTH } from '@/features/DataChangeRequest/_schemas/HcpSchemas/LicensesSection.validation';
import { DcrFormController } from '@/features/DataChangeRequest/DcrFormController/DcrFormController';
import type { RequirePair } from '@/utils/typescript/RequirePair';

import { licensesSectionCardStyles as sx } from './LicenseSectionCard.styles';

type LicenseData = NonNullable<HcpDcrValuesIn['licenses']>[number];

const POTENTIAL_DIRTY_FIELDS: (keyof LicenseData)[] = [
	'licenseNumber_V',
	'typeValue_VId',
	'expirationDate_V',
	'bestStateLicense_V',
	'licenseStatus_V'
];

type Props = {
	index: number;
	isLastItem: boolean;
	isExpanded: boolean;
	originalData?: LicenseData;
	isNewCard: boolean;
} & RequirePair<'canBeRemoved', 'onRemove', () => void>;

export const LicensesSectionCard = ({
	index,
	onRemove,
	canBeRemoved,
	isLastItem,
	isExpanded,
	originalData,
	isNewCard
}: Props) => {
	const { t } = useTranslation();
	const { licenseFields } = useDcrFields();
	const {
		control,
		formState: { errors }
	} = useFormContext<HcpDcrValuesIn>();
	const [expanded, setExpanded] = useState(isExpanded);
	const section = 'licenses';

	const {
		picklists: { licenseStatusOptions }
	} = useDcrContext();

	const values = useWatch({
		control,
		name: `licenses.${index}`
	});

	const headerTitle = (values?.licenseNumber_V as string) || t('New License');

	const checkIsDirty = useDirtyFieldCheck<LicenseData>(
		isNewCard,
		originalData,
		['bestStateLicense_V']
	);

	const hasAnyFieldDirty = useMemo(() => {
		if (!values) return false;
		return POTENTIAL_DIRTY_FIELDS.some(key => checkIsDirty(key, values[key]));
	}, [values, checkIsDirty]);

	useEffect(() => {
		setExpanded(isExpanded);
	}, [isExpanded]);

	const hasError = useMemo(() => {
		return !!errors.addresses?.[index];
	}, [errors.addresses, index]);

	return (
		<Box sx={sx.root}>
			<Box
				sx={() => sx.header(isLastItem)}
				onClick={() => setExpanded(!expanded)}
			>
				<Box sx={sx.headerContent(hasAnyFieldDirty, hasError)}>
					<ExpandMoreIcon sx={sx.expandIcon(expanded)} />
					<Typography sx={sx.headerText}>{headerTitle}</Typography>
				</Box>
				{canBeRemoved && (
					<Box sx={sx.removeButton}>
						<RemoveFormFieldButton type='button' onClick={onRemove} />
					</Box>
				)}
			</Box>

			<Collapse in={expanded}>
				<Box sx={sx.content(expanded)}>
					<Box sx={sx.gridContainer}>
						{/* Row 1: ID + Primary Toggle */}
						<DcrFormController
							name={`licenses.${index}.licenseNumber_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormTextField
									id={field.name}
									name={field.name}
									label={licenseFields.licenseNumber_V.label}
									defaultValue={(field.value as string) ?? ''}
									onChange={field.onChange}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('licenseNumber_V', field.value)}
								/>
							)}
						/>

						{/* Row 2: License Degree (Read Only) */}
						<FormReadOnlyField
							label={licenseFields.licenseDegree_V.label}
							section={section}
							value={values?.licenseDegree_V}
							name={`licenses.${index}.licenseDegree_V`}
						/>

						{/* Row 3: Licensing Authority (Editable) */}
						<DcrFormController
							name={`licenses.${index}.typeValue_VId`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormTextField
									id={field.name}
									name={field.name}
									label={licenseFields.typeValue_VId.label}
									defaultValue={(field.value as string) ?? ''}
									onChange={field.onChange}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('typeValue_VId', field.value)}
									maxLength={LICENSING_AUTHORITY_MAX_LENGTH}
								/>
							)}
						/>

						{/* Note(Filip): As of 29.01 - Jonas wants the static fields to be hidden for the time being*/}
						{/* <FormReadOnlyField
							label={t('License Type')}
							value={values?.type_V}
							name={`licenses.${index}.type_V`}
							section={section}
						/>

						<FormReadOnlyField
							label={t('Grace Expiry')}
							value={values?.gracePeriod_V}
							name={`licenses.${index}.gracePeriod_V`}
							section={section}
						/> */}

						{/* Row 6: Expiration Date (Editable) */}
						<DcrFormController
							name={`licenses.${index}.expirationDate_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormTextField
									id={field.name}
									name={field.name}
									label={licenseFields.expirationDate_V.label}
									isDateField={true}
									defaultValue={(field.value as string) ?? ''}
									onChange={field.onChange}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('expirationDate_V', field.value)}
								/>
							)}
						/>

						<DcrFormController
							name={`licenses.${index}.licenseStatus_V`}
							section={section}
							control={control}
							render={({ field, fieldState }) => (
								<FormAutocompleteField
									id={field.name}
									name={field.name}
									section={section}
									label={licenseFields.licenseStatus_V.label}
									options={licenseStatusOptions ?? []}
									handleOnDropdownItemSelect={item => {
										const option = licenseStatusOptions?.find(
											opt => opt.id === item?.id
										);
										field.onChange(option?.name ?? item?.id ?? '');
									}}
									defaultValue={(field.value as string) ?? ''}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('licenseStatus_V', field.value)}
									canBeRemoved={false}
									canAddNewItem={false}
								/>
							)}
						/>

						{/* Note(Filip): As of 29.01 - Jonas wants the static fields to be hidden for the time being*/}
						{/* <FormReadOnlyField
							label={t('Licensing Body')}
							value={values?.body_V}
							name={`licenses.${index}.body_V`}
							section={section}
						/>

						<FormReadOnlyField
							label={t('License Certification')}
							value={values?.licenseCertification_V}
							name={`licenses.${index}.licenseCertification_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('Sample Eligibility')}
							value={values?.licenseEligibility_V}
							name={`licenses.${index}.licenseEligibility_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('License Status Condition')}
							value={values?.licenseStatusCondition_V}
							name={`licenses.${index}.licenseStatusCondition_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('License SubType')}
							value={values?.licenseSubtype_V}
							name={`licenses.${index}.licenseSubtype_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('Comprehensive Prescriptive Authority')}
							value={values?.comprehensivePrescriptiveAuthority_V}
							name={`licenses.${index}.comprehensivePrescriptiveAuthority_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('Comprehensive Sample Eligibility')}
							value={values?.comprehensiveSampleEligibility_V}
							name={`licenses.${index}.comprehensiveSampleEligibility_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('Collaborative Agreement Required')}
							value={values?.collabAgreeRequired_V}
							name={`licenses.${index}.collabAgreeRequired_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('Controlled Substances Prescriptive Authority')}
							value={values?.ctrlSubstancePrescriptive_V}
							name={`licenses.${index}.ctrlSubstancePrescriptive_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('Controlled Substances Sample Eligibility')}
							value={values?.ctrlSubstanceSample_V}
							name={`licenses.${index}.ctrlSubstanceSample_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('DEA Activity Code')}
							value={values?.deaBusinessActivityCode_V}
							name={`licenses.${index}.deaBusinessActivityCode_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('DEA Payment Indicator')}
							value={values?.deaPaymentIndicator_V}
							name={`licenses.${index}.deaPaymentIndicator_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('Drug Schedule Details')}
							value={values?.drugSchedule_V}
							name={`licenses.${index}.drugSchedule_V`}
							section={section}
						/>
						<FormReadOnlyField
							label={t('Address from License Source')}
							value={values?.sourceAddress_V}
							name={`licenses.${index}.sourceAddress_V`}
							section={section}
						/> */}
					</Box>
				</Box>
			</Collapse>
		</Box>
	);
};
